import type {
  CompressImageOnServerArgs,
  CompressImageOnServerResult,
} from './types';

const COMPRESS_SERVER_URL = import.meta.env.VITE_COMPRESS_SERVER_URL as string;
const COMPRESS_API_KEY = import.meta.env.VITE_COMPRESS_API_KEY as
  | string
  | undefined;

const parseHeaderAsNumber = (
  headers: Headers,
  name: string,
): number | null => {
  const value = headers.get(name);
  if (value === null) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const compressImageOnServer = async ({
  file,
  scaleFactor,
  quality,
  outputFormat,
}: CompressImageOnServerArgs): Promise<CompressImageOnServerResult> => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('scaleFactor', String(scaleFactor));
  formData.append('quality', String(quality));
  formData.append('outputFormat', outputFormat);

  const requestHeaders: HeadersInit = {};
  if (COMPRESS_API_KEY) requestHeaders['X-API-Key'] = COMPRESS_API_KEY;

  const response = await fetch(`${COMPRESS_SERVER_URL}/compress`, {
    method: 'POST',
    headers: requestHeaders,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || `Server error: ${response.status}`);
  }

  const blob = await response.blob();
  const { headers } = response;

  const originalWidth = parseHeaderAsNumber(headers, 'X-Original-Width') ?? 0;
  const originalHeight = parseHeaderAsNumber(headers, 'X-Original-Height') ?? 0;
  const outputWidth = parseHeaderAsNumber(headers, 'X-Output-Width') ?? 0;
  const outputHeight = parseHeaderAsNumber(headers, 'X-Output-Height') ?? 0;
  const requestedWidth = parseHeaderAsNumber(headers, 'X-Requested-Width') ?? 0;
  const requestedHeight = parseHeaderAsNumber(headers, 'X-Requested-Height') ?? 0;

  const outputFile = new File([blob], `compressed.${outputFormat}`, {
    type: blob.type,
  });

  return {
    blob,
    file: outputFile,
    originalWidth,
    originalHeight,
    outputWidth,
    outputHeight,
    requestedWidth,
    requestedHeight,
  };
};
